import React from 'react';
import { motion } from 'framer-motion';
import { Shield, Lock, Database, Trash2, Mail, ExternalLink } from 'lucide-react';
import Header from './Header';
import Footer from './Footer';

const PoliticaDePrivacidadePage = () => {
  const navigateTo = (url) => {
    window.location.href = url;
  };

  const sections = [
    {
      icon: Database,
      title: '1. Quais Informações Coletamos',
      content: [
        'O MEU MEI pode recolher, gravar e utilizar em conexão com seu próprio site e/ou sites de parceiros homologados as informações pessoais dos usuários, incluindo, mas não se limitando ao nome da empresa, CNPJ da empresa usuária, endereço, número de telefone, interesses, atividades, tempo de abertura da empresa, etc.',
        'Os dados coletados e existentes em nossos sistemas são (I) informações que o usuário se prontifica a fornecer através de cadastro (dados pessoais, dados financeiros, e dados de acesso), emissão de notas fiscais ou importação de extratos bancários e (II) informações obtidas automaticamente.',
        'Os dados que os usuários se prontificam a fornecer são aqueles definidos como dados pessoais, dados financeiros e dados de acesso e que são preenchidas no momento do cadastro necessário para utilização da Plataforma.',
        'O Meu Mei coleta ainda os dados financeiros também incluídos na Plataforma, através de importação de nota fiscal, lançamentos manuais feitos pelo usuário. E, dessa forma, coletamos informações a respeito dos dados financeiros, por exemplo, o pagamento de um fornecedor, data de vencimento.'
      ]
    },
    {
      icon: Lock,
      title: '2. O Que Fazemos com as Informações Coletadas',
      content: [
        'AS INFORMAÇÕES NÃO SERÃO DIVULGADAS, VENDIDAS OU ALUGADAS PARA QUALQUER ORGANIZAÇÃO OU ENTIDADE, TAMPOUCO COM ELAS SERÁ FEITA PERMUTA SEM ANTES COMUNICAR AO USUÁRIO E OBTER SEU CONSENTIMENTO EXPRESSO, COM EXCEÇÃO DE INFORMAÇÕES EXIGIDAS POR LEIS OU POR ORDEM JUDICIAL.',
        'O principal objetivo do Meu Mei ao acessar dados individuais ou consolidados de seus usuários é oferecer um serviço preciso que simplifique a rotina e potencialize a gestão de sua empresa. Por esse motivo, tanto as informações fornecidas voluntariamente pelos usuários quanto os dados coletados automaticamente são utilizadas para avaliar o uso da plataforma, administrar os serviços prestados, aprimorá-los continuamente e identificar possíveis necessidades dos usuários.',
        'O Meu Mei envia e-mails e notificações via app ou redes sociais, e o usuário desde já autoriza que ele o faça, com alertas e comunicados relacionados às funções da plataforma no intuito permitir que o usuário explore todas as suas funcionalidades, bem como com informações relevantes à planejamento financeiro ou questões referentes à gestão empresarial, entre outros tipos.',
        'AO PREENCHER O FORMULÁRIO E FORNECER AS INFORMAÇÕES AO MEU MEI, O USUÁRIO DECLARA QUE PERMITE O PROCESSAMENTO DE DADOS PESSOAIS CONFORME ESTABELECIDO NESTE TERMO.'
      ],
      highlights: [0, 3]
    },
    {
      icon: Trash2,
      title: '3. Armazenamento e Exclusão de Dados Pessoais',
      content: [
        'O usuário poderá ter acesso às suas informações pessoais que inclui no momento do cadastro e às informações que incluiu na plataforma (notas fiscais, certidões e outros documentos) através do acesso à plataforma.',
        'O Meu Mei armazenará todos os dados e informações pelo tempo que entender necessário e até a exclusão de sua conta e rescisão de contrato de prestação de serviços. O usuário poderá realizar a exclusão de sua conta a qualquer momento, bastando fazê-lo também através da plataforma, respeitadas as obrigações existentes no contrato de prestação de serviços.',
        'Quando o usuário exclui sua conta e rescinde o contrato, os dados pessoais serão excluídos, porém são mantidas as informações de guarda obrigatória, conforme determinação legal. Caso o usuário tenha qualquer dúvida quanto à presente política de privacidade, poderá entrar em contato com o Meu Mei através da plataforma ou atendimento online junto ao site www.meumei.net.'
      ]
    }
  ];

  return (
    <div className="min-h-screen font-sans">
      {/* Header padronizado */}
      <Header />

      {/* Hero Section */}
      <section 
        className="relative py-32 md:py-40 px-4 overflow-hidden"
        style={{
          background: 'linear-gradient(135deg, #10B981 0%, #059669 50%, #047857 100%)',
        }}
      >
        {/* Elementos decorativos de fundo */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div 
            className="absolute -top-40 -right-40 w-96 h-96 rounded-full opacity-20"
            style={{ background: 'radial-gradient(circle, #ffffff 0%, transparent 70%)' }}
          />
          <div 
            className="absolute -bottom-20 -left-20 w-72 h-72 rounded-full opacity-10"
            style={{ background: 'radial-gradient(circle, #ffffff 0%, transparent 70%)' }}
          />
          {/* Padrão de escudo */}
          <div className="absolute inset-0 flex items-center justify-center opacity-5">
            <Shield size={400} />
          </div>
        </div>

        <div className="max-w-7xl mx-auto relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center text-white"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2, duration: 0.5, type: 'spring' }}
              className="w-24 h-24 mx-auto mb-8 rounded-full flex items-center justify-center"
              style={{
                background: 'rgba(255, 255, 255, 0.2)',
                backdropFilter: 'blur(10px)',
              }}
            >
              <Shield size={48} />
            </motion.div>
            <h1 className="text-5xl md:text-7xl font-bold mb-6">
              Política de Privacidade
            </h1>
            <p className="text-xl md:text-2xl max-w-3xl mx-auto leading-relaxed opacity-90">
              Transparência e segurança no tratamento dos seus dados
            </p>
          </motion.div>
        </div>

        {/* Wave decoration */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path 
              d="M0 120L60 110C120 100 240 80 360 70C480 60 600 60 720 65C840 70 960 80 1080 85C1200 90 1320 90 1380 90L1440 90V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z" 
              fill="white"
            />
          </svg>
        </div>
      </section>

      {/* Content Sections */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-4xl mx-auto space-y-12">
          {sections.map((section, index) => (
            <motion.div
              key={section.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1, duration: 0.6 }}
              className="bg-white rounded-2xl p-8 md:p-10 relative overflow-hidden"
              style={{
                boxShadow: '0 10px 40px rgba(0, 0, 0, 0.08)',
                border: '1px solid rgba(16, 185, 129, 0.1)',
              }}
            >
              {/* Decoração de fundo */}
              <div 
                className="absolute top-0 right-0 w-32 h-32 rounded-full opacity-5 pointer-events-none"
                style={{
                  background: 'radial-gradient(circle, #10B981 0%, transparent 70%)',
                  transform: 'translate(30%, -30%)',
                }}
              />

              {/* Header da seção */}
              <div className="flex items-start gap-4 mb-8">
                <div 
                  className="w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0"
                  style={{
                    background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
                    boxShadow: '0 8px 20px rgba(16, 185, 129, 0.3)',
                  }}
                >
                  <section.icon className="text-white" size={24} />
                </div>
                <h2 className="text-2xl md:text-3xl font-bold text-[#2C5E50]">
                  {section.title}
                </h2>
              </div>
              
              {/* Conteúdo */}
              <div className="space-y-4 text-gray-600 text-base md:text-lg leading-relaxed pl-0 md:pl-[72px]">
                {section.content.map((paragraph, pIndex) => (
                  <motion.p 
                    key={pIndex}
                    initial={{ opacity: 0, x: -10 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.1 + pIndex * 0.05, duration: 0.4 }}
                    className={
                      section.highlights?.includes(pIndex)
                        ? 'font-medium bg-gradient-to-r from-green-50 to-emerald-50 p-5 rounded-xl border-l-4 border-[#10B981] text-gray-700'
                        : ''
                    }
                  >
                    {paragraph}
                  </motion.p>
                ))}
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center bg-white rounded-3xl p-12 md:p-16 relative overflow-hidden"
            style={{
              boxShadow: '0 20px 60px rgba(16, 185, 129, 0.1)',
            }}
          >
            {/* Decoração */}
            <div 
              className="absolute top-0 left-0 w-64 h-64 rounded-full opacity-20 pointer-events-none"
              style={{
                background: 'radial-gradient(circle, #10B981 0%, transparent 70%)',
                transform: 'translate(-30%, -30%)',
              }}
            />
            <div 
              className="absolute bottom-0 right-0 w-48 h-48 rounded-full opacity-20 pointer-events-none"
              style={{
                background: 'radial-gradient(circle, #10B981 0%, transparent 70%)',
                transform: 'translate(30%, 30%)',
              }}
            />

            <motion.div
              initial={{ scale: 0 }}
              whileInView={{ scale: 1 }}
              viewport={{ once: true }}
              transition={{ type: 'spring', delay: 0.2 }}
              className="w-20 h-20 mx-auto mb-6 rounded-full flex items-center justify-center"
              style={{
                background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
                boxShadow: '0 8px 30px rgba(16, 185, 129, 0.4)',
              }}
            >
              <Mail className="text-white" size={32} />
            </motion.div>

            <h3 className="text-2xl md:text-3xl font-bold text-[#2C5E50] mb-4 relative z-10">
              Dúvidas sobre nossa Política de Privacidade?
            </h3>
            <p className="text-gray-600 text-lg mb-8 relative z-10">
              Entre em contato conosco através da plataforma ou pelo nosso atendimento online.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center relative z-10">
              <motion.a 
                href="mailto:contato@oxygengroup.com.br"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="inline-flex items-center gap-2 px-8 py-4 text-white font-semibold rounded-full transition-all"
                style={{
                  background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                  boxShadow: '0 8px 30px rgba(16, 185, 129, 0.4)',
                }}
              >
                <Mail size={20} />
                Entrar em Contato
              </motion.a>
              
              <motion.a 
                href="https://app.mei.oxygenhub.app/register"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="inline-flex items-center gap-2 px-8 py-4 font-semibold rounded-full border-2 border-[#10B981] text-[#10B981] hover:bg-[#10B981] hover:text-white transition-all"
              >
                <ExternalLink size={20} />
                Acessar Plataforma
              </motion.a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Última atualização */}
      <section className="py-8 px-4 bg-white border-t border-gray-100">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-gray-500 text-sm">
            Última atualização: Janeiro de 2025
          </p>
        </div>
      </section>

      {/* Footer padronizado */}
      <Footer />
    </div>
  );
};

export default PoliticaDePrivacidadePage;